import { useTranslation } from "react-i18next";

import {
  formatAttrValue,
  parseSeverity,
  type RuleDiffSeedEntry,
  type RuleDiffSeverity,
} from "@/lib/rulediff-seed-api";

/**
 * apps/web :: components :: RuleDiffAttributeCard
 *
 * Renders a single rule-diff attribute (one of the 10 canonical
 * attribute names) for the rule-diff browse page. Pure leaf — no
 * data-fetching; the parent passes a {@link RuleDiffSeedEntry}.
 *
 * Visual treatment branches on the severity parsed from
 * `reviewer_notes` ({@link parseSeverity}):
 *   - `critical` (DOMINANT / SAFETY-CRITICAL): rose-tinted border,
 *     warning icon, attention-grabbing for LHS countries on RHS
 *     destinations + the recurring move-over / school-bus diffs
 *   - `warning` (BEHAVIORAL DIFF / NOTABLE): amber border
 *   - `match` (RARE MATCH / no diff): green border with calmer chrome
 *   - `neutral` (default): no special border
 *
 * Accessibility (WCAG 2.2 AA):
 *   - Card is a `<section>` with `aria-labelledby` → the attribute
 *     name heading
 *   - Severity badge has visible-text "SAFETY-CRITICAL" / "BEHAVIORAL"
 *     etc. for screen readers (color is not the only signal)
 *   - Confidence pill exposes its value via plain text (e.g. "0.95")
 *     not a chart
 *   - Source link has `rel="noopener noreferrer" target="_blank"` +
 *     a visible (sr-only) "(opens in new tab)" hint
 */
export interface RuleDiffAttributeCardProps {
  entry: RuleDiffSeedEntry;
}

const SEVERITY_CLASSES: Record<RuleDiffSeverity, string> = {
  critical: "border-l-4 border-rose-500 bg-rose-50/50",
  warning: "border-l-4 border-amber-500 bg-amber-50/40",
  match: "border-l-4 border-emerald-500 bg-emerald-50/30",
  neutral: "border-l-2 border-neutral-300",
};

const SEVERITY_LABEL_KEYS: Record<RuleDiffSeverity, string> = {
  critical: "ruleDiffBrowse.severity.critical",
  warning: "ruleDiffBrowse.severity.warning",
  match: "ruleDiffBrowse.severity.match",
  neutral: "ruleDiffBrowse.severity.neutral",
};

const SEVERITY_BADGE_CLASSES: Record<RuleDiffSeverity, string> = {
  critical: "bg-rose-100 text-rose-900",
  warning: "bg-amber-100 text-amber-900",
  match: "bg-emerald-100 text-emerald-900",
  neutral: "bg-neutral-100 text-neutral-700",
};

export default function RuleDiffAttributeCard({
  entry,
}: RuleDiffAttributeCardProps) {
  const { t } = useTranslation();
  const severity = parseSeverity(entry.reviewer_notes);
  const formattedValue = formatAttrValue(entry);
  const headingId = `rule-diff-attr-${entry.attr_name}`;

  return (
    <section
      data-testid={`rule-diff-attr-card-${entry.attr_name}`}
      data-severity={severity}
      aria-labelledby={headingId}
      className={`rounded-md p-md sm:p-lg ${SEVERITY_CLASSES[severity]}`}
    >
      <div className="flex flex-col gap-sm sm:flex-row sm:items-baseline sm:justify-between">
        <h2
          id={headingId}
          className="font-display text-lg font-semibold text-brand-primary sm:text-xl"
        >
          {t(`ruleDiffBrowse.attr.${entry.attr_name}`, {
            defaultValue: entry.attr_name,
          })}
        </h2>
        {severity !== "neutral" && (
          <span
            className={`inline-flex shrink-0 items-center gap-xs rounded-full px-sm py-xs text-xs font-semibold uppercase tracking-wide ${SEVERITY_BADGE_CLASSES[severity]}`}
            data-testid={`rule-diff-severity-${entry.attr_name}`}
          >
            {t(SEVERITY_LABEL_KEYS[severity])}
          </span>
        )}
      </div>

      <div className="mt-sm text-base">
        <span className="text-neutral-600">
          {t("ruleDiffBrowse.valueLabel")}:
        </span>{" "}
        <span className="font-semibold text-neutral-900">{formattedValue}</span>
      </div>

      <p className="mt-sm text-sm text-neutral-800">{entry.reviewer_notes}</p>

      <footer className="mt-md flex flex-wrap items-center gap-md text-xs text-neutral-600">
        <span
          aria-label={t("ruleDiffBrowse.confidenceAriaLabel", {
            value: entry.confidence,
          })}
        >
          {t("ruleDiffBrowse.confidenceLabel")}: {entry.confidence.toFixed(2)}
        </span>
        <a
          href={entry.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("ruleDiffBrowse.sourceLink")}
          <span className="sr-only"> {t("ruleDiffBrowse.opensInNewTab")}</span>
        </a>
      </footer>
    </section>
  );
}
